import { Home, Medal, RotateCcw, Sparkles, Trophy } from "lucide-react";
import {
  getLeaderboard,
  getVoteHighlights,
} from "../../game-engine/simulation";
import type {
  GameDefinition,
  RoundResult,
  SimulatedParticipant,
} from "../../game-engine/types";
import { ParticipantAvatar } from "../ParticipantAvatar/ParticipantAvatar";
import styles from "./GameSummary.module.css";

interface GameSummaryProps {
  definition: GameDefinition;
  participants: readonly SimulatedParticipant[];
  results: readonly RoundResult[];
  onReplay: () => void;
  onExit: () => void;
}

export function GameSummary({
  definition,
  participants,
  results,
  onReplay,
  onExit,
}: GameSummaryProps) {
  const isLeaderboard = definition.summary.type === "leaderboard";
  const leaderboard = isLeaderboard
    ? getLeaderboard(participants, results)
    : [];
  const highlights = !isLeaderboard
    ? getVoteHighlights(definition, participants, results)
    : [];

  return (
    <main className={styles.summary}>
      <header className={styles.heading}>
        <span className={styles.trophyMark} aria-hidden="true">
          {isLeaderboard ? <Trophy /> : <Sparkles />}
        </span>
        <span className={styles.eyebrow}>Spelet är slut</span>
        <h1 data-session-heading tabIndex={-1}>
          {definition.summary.title}
        </h1>
        <p>{definition.summary.subtitle}</p>
      </header>

      {isLeaderboard ? (
        <section
          className={styles.leaderboard}
          aria-labelledby="leaderboard-heading"
        >
          <h2 id="leaderboard-heading">Slutställning</h2>
          <ol>
            {leaderboard.map((entry, index) => (
              <li
                className={styles.leaderboardEntry}
                data-podium={index < 3 ? String(index + 1) : undefined}
                key={entry.participant.id}
              >
                <span className={styles.rank}>
                  {index < 3 ? <Medal aria-hidden="true" /> : null}
                  <span className={index < 3 ? "visually-hidden" : undefined}>
                    {index + 1}
                  </span>
                </span>
                <ParticipantAvatar
                  participant={entry.participant}
                  size={index < 3 ? "medium" : "small"}
                  showName={false}
                />
                <span className={styles.playerName}>
                  {entry.participant.name}
                  <small>{entry.correctAnswers} rätt</small>
                </span>
                <strong>{entry.score.toLocaleString("sv-SE")} p</strong>
              </li>
            ))}
          </ol>
        </section>
      ) : (
        <section
          className={styles.highlights}
          aria-labelledby="highlights-heading"
        >
          <h2 id="highlights-heading">Kvällens highlights</h2>
          <ol>
            {highlights.map((highlight, index) => (
              <li key={highlight.roundId}>
                <span className={styles.highlightNumber}>
                  {String(index + 1).padStart(2, "0")}
                </span>
                <p>{highlight.prompt}</p>
                <div>
                  <ParticipantAvatar
                    participant={highlight.participant}
                    size="medium"
                    showName={false}
                  />
                  <span>
                    <strong>{highlight.participant.name}</strong>
                    {highlight.votes} av {participants.length} röster
                  </span>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      <div className={styles.actions}>
        <button className={styles.replayButton} type="button" onClick={onReplay}>
          <RotateCcw aria-hidden="true" />
          Spela igen
        </button>
        <button className={styles.exitButton} type="button" onClick={onExit}>
          <Home aria-hidden="true" />
          Till startsidan
        </button>
      </div>
    </main>
  );
}
